import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Grid, Card,
    alpha, Stack, Chip, Avatar, Paper, Fade, Grow,
} from '@mui/material';
import {
    School as SchoolIcon,
    MenuBook as BookIcon,
    EmojiEvents as TrophyIcon,
    Groups as GroupsIcon,
    LaptopMac as LmsIcon,
    CloudUpload as UploadIcon,
    Assignment as CourseIcon,
    Person as PersonIcon,
    Security as SecurityIcon,
    Dashboard as DashboardIcon,
    Psychology as BrainIcon,
    Quiz as QuizIcon,
    BarChart as ChartIcon,
    TrendingUp as ProgressIcon,
    AccountTree as StructureIcon,
    Notifications as NotificationIcon,
    CalendarMonth as PlannerIcon,
    Rule as ReviewIcon,
    AutoAwesome as AIIcon,
    ListAlt as TestIcon,
    History as HistoryIcon,
    School as ExamIcon,
    Translate as LanguageIcon,
    Calculate as AptitudeIcon,
    Public as GeneralIcon,
} from '@mui/icons-material';
import Navbar from '../Navbar';
import HomeFooter from '../HomeFooter';

const BRAND_GRADIENT = 'linear-gradient(135deg, #1d748b 0%, #52d370 100%)';
const TNPSC_COLOR = '#d63384';
const GROUP1_COLOR = '#6f42c1';
const GROUP2_COLOR = '#0d6efd';
const GROUP4_COLOR = '#fd7e14';
const OTHER_COLOR = '#198754';

// ==================== TNPSC SMART AI DATA ====================
const tnpscData = {
    name: 'TNPSC Smart AI',
    shortName: 'TS',
    tagline: 'AI-Powered TNPSC Preparation',
    color: TNPSC_COLOR,
    type: 'Competitive Examination Learning & Assessment Management System',
    about: 'TNPSC Smart AI is a dedicated digital learning and examination management platform designed to support aspirants preparing for Tamil Nadu Public Service Commission (TNPSC) competitive examinations.',
    lmsIntro: 'We developed and implemented a centralized Learning Management System (LMS) to manage student registration, examinations, courses, subjects, syllabus, units, topics, learning resources, question banks, AI-assisted question generation, online tests, mock examinations, results and student performance.',
    lmsFeatures: [
        {
            title: 'Student Registration',
            desc: 'Enables aspirants to create and manage their personalized learning accounts.',
            icon: <PersonIcon />,
        },
        {
            title: 'Exam Management',
            desc: 'Organizes different TNPSC examinations and examination categories.',
            icon: <ExamIcon />,
        },
        {
            title: 'Course Management',
            desc: 'Provides structured courses and preparation programmes for TNPSC aspirants.',
            icon: <CourseIcon />,
        },
        {
            title: 'Subject Management',
            desc: 'Organizes subjects according to the selected examination.',
            icon: <BookIcon />,
        },
        {
            title: 'Syllabus Management',
            desc: 'Provides examination-wise syllabus and preparation structure.',
            icon: <StructureIcon />,
        },
        {
            title: 'Unit Management',
            desc: 'Divides subjects into structured units for systematic preparation.',
            icon: <DashboardIcon />,
        },
        {
            title: 'Topic Management',
            desc: 'Organizes units into detailed topics and sub-topics.',
            icon: <DashboardIcon />,
        },
        {
            title: 'Learning Materials',
            desc: 'Provides notes, PDFs, study materials and other educational resources.',
            icon: <UploadIcon />,
        },
        {
            title: 'Question Bank',
            desc: 'Maintains questions categorized by examination, subject, unit, topic, question type and difficulty level.',
            icon: <QuizIcon />,
        },
        {
            title: 'AI Question Generation',
            desc: 'Helps generate practice questions and examination-oriented content using AI-assisted workflows.',
            icon: <AIIcon />,
        },
        {
            title: 'Question Review & Approval',
            desc: 'Provides a structured workflow for creating, reviewing, approving and publishing questions.',
            icon: <ReviewIcon />,
        },
        {
            title: 'Online Practice Tests',
            desc: 'Enables aspirants to practice questions through topic-wise, subject-wise and examination-wise tests.',
            icon: <LmsIcon />,
        },
        {
            title: 'Mock Examinations',
            desc: 'Provides simulated examinations for competitive exam preparation.',
            icon: <TestIcon />,
        },
        {
            title: 'Assessment Management',
            desc: 'Manages tests, examinations, marks, attempts and assessments.',
            icon: <ChartIcon />,
        },
        {
            title: 'Result Management',
            desc: 'Provides detailed test and examination results.',
            icon: <HistoryIcon />,
        },
        {
            title: 'Performance Analysis',
            desc: 'Helps aspirants identify strengths, weaknesses, accuracy and improvement areas.',
            icon: <ChartIcon />,
        },
        {
            title: 'Progress Tracking',
            desc: 'Tracks preparation progress across subjects, units and topics.',
            icon: <ProgressIcon />,
        },
        {
            title: 'Study Planner',
            desc: 'Helps aspirants organize their preparation schedule and daily learning activities.',
            icon: <PlannerIcon />,
        },
        {
            title: 'Notifications',
            desc: 'Provides updates about examinations, tests, learning activities and important announcements.',
            icon: <NotificationIcon />,
        },
        {
            title: 'Student Dashboard',
            desc: 'Provides a centralized overview of courses, tests, results, progress and preparation activities.',
            icon: <DashboardIcon />,
        },
    ],
    examAreas: [
        {
            exam: 'TNPSC Group I',
            color: GROUP1_COLOR,
            icon: <TrophyIcon />,
            subjects: [
                { name: 'Preliminary Examination', icon: <ExamIcon /> },
                { name: 'Main Examination', icon: <ExamIcon /> },
                { name: 'General Studies', icon: <GeneralIcon /> },
                { name: 'Aptitude & Mental Ability', icon: <AptitudeIcon /> },
                { name: 'Tamil / Language', icon: <LanguageIcon /> },
            ],
        },
        {
            exam: 'TNPSC Group II / IIA',
            color: GROUP2_COLOR,
            icon: <GroupsIcon />,
            subjects: [
                { name: 'Group II', icon: <ExamIcon /> },
                { name: 'Group IIA', icon: <ExamIcon /> },
                { name: 'General Studies', icon: <GeneralIcon /> },
                { name: 'Aptitude & Mental Ability', icon: <AptitudeIcon /> },
                { name: 'Language-related', icon: <LanguageIcon /> },
            ],
        },
        {
            exam: 'TNPSC Group IV',
            color: GROUP4_COLOR,
            icon: <SchoolIcon />,
            subjects: [
                { name: 'General Studies', icon: <GeneralIcon /> },
                { name: 'Aptitude & Mental Ability', icon: <AptitudeIcon /> },
                { name: 'Tamil Eligibility Test', icon: <LanguageIcon /> },
            ],
        },
        {
            exam: 'Other TNPSC Examinations',
            color: OTHER_COLOR,
            icon: <ExamIcon />,
            subjects: [
                { name: 'Technical Examinations', icon: <ExamIcon /> },
                { name: 'Departmental Examinations', icon: <ExamIcon /> },
                { name: 'Other Recruitment Exams', icon: <ExamIcon /> },
            ],
        },
    ],
    questionBankFlow: [
        'Exam',
        'Subject',
        'Unit',
        'Topic',
        'Question',
        'Options',
        'Correct Answer',
        'Explanation',
        'Difficulty',
        'Review',
        'Approval',
        'Publish',
    ],
    questionTypes: [
        'Multiple Choice Questions',
        'Multiple Correct Questions',
        'True / False',
        'Match the Following',
        'Fill in the Blanks',
    ],
    aiFeatures: [
        'AI Question Generation',
        'Question Explanation',
        'Practice Question Creation',
        'Topic-based Question Generation',
        'Difficulty-based Question Generation',
        'Learning Content Assistance',
        'Personalized Practice Support',
    ],
    academicStructure: [
        'Exam',
        'Course',
        'Subject',
        'Syllabus',
        'Unit',
        'Topic',
        'Learning Material',
        'Question Bank',
        'Practice Test',
        'Mock Exam',
        'Result',
        'Performance Analysis',
    ],
    quickInfo: [
        // { label: 'Client', value: 'TNPSC Smart AI', icon: <SchoolIcon /> },
        { label: 'Target', value: 'TNPSC Aspirants', icon: <GroupsIcon /> },
        { label: 'Solution', value: 'Exam Learning & Assessment', icon: <LmsIcon /> },
        { label: 'Platform', value: 'TNPSC Smart AI LMS', icon: <BrainIcon /> },
    ],
};

const NewTNPSCSmartAIPage = () => {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    if (!mounted) return null;

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#f8f9fa' }}>
            <Navbar />

            {/* ==================== HERO ==================== */}
            <Box
                sx={{
                    background: BRAND_GRADIENT,
                    pt: { xs: 8, md: 10 },
                    pb: { xs: 12, md: 16 },
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: '10%', right: '5%', width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.25) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'absolute', bottom: '5%', left: '5%', width: 250, height: 250, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)' }} />

                <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, mt: 2 }}>
                    <Fade in timeout={800}>
                        <Box>
                            <Chip
                                label="AI-POWERED LMS"
                                sx={{
                                    mb: 3,
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '0.95rem',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                }}
                            />

                            {/* ==================== LOGO + TITLE (SAME ROW) ==================== */}
                            <Stack
                                direction="row"
                                spacing={{ xs: 2, md: 3 }}
                                alignItems="center"
                                sx={{ mb: 2 }}
                            >
                                <Box
                                    component="img"
                                    src="/images/tnpsc_logo.png"
                                    alt="TNPSC Smart AI Logo"
                                    sx={{
                                        height: { xs: 60, sm: 75, md: 100 },
                                        width: 'auto',
                                        flexShrink: 0,
                                        objectFit: 'contain',
                                        filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))',
                                    }}
                                />

                                <Typography
                                    variant="h1"
                                    sx={{
                                        fontSize: { xs: '1.8rem', sm: '2.4rem', md: '3.8rem' },
                                        fontWeight: 800,
                                        color: 'white',
                                        lineHeight: 1.15,
                                        m: 0,
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {tnpscData.name}
                                </Typography>
                            </Stack>

                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'rgba(255,255,255,0.95)',
                                    maxWidth: 850,
                                    fontWeight: 400,
                                    fontSize: { xs: '1.05rem', md: '1.3rem' },
                                    mb: 3,
                                }}
                            >
                                {tnpscData.type}
                            </Typography>

                            <Stack direction="row" spacing={2} flexWrap="wrap">
                                <Chip
                                    icon={<AIIcon sx={{ color: 'white !important' }} />}
                                    label="AI Question Generation"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, fontSize: '0.95rem' }}
                                />
                                <Chip
                                    icon={<QuizIcon sx={{ color: 'white !important' }} />}
                                    label="Question Bank"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, fontSize: '0.95rem' }}
                                />
                                <Chip
                                    icon={<TestIcon sx={{ color: 'white !important' }} />}
                                    label="Mock Exams"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, fontSize: '0.95rem' }}
                                />
                            </Stack>
                        </Box>
                    </Fade>
                </Container>
            </Box>

            {/* ==================== OVERVIEW CARD ==================== */}
            <Container maxWidth="xl" sx={{ mt: -10, mb: 5, position: 'relative', zIndex: 2 }}>
                <Grow in timeout={1000}>
                    <Paper elevation={24} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, bgcolor: 'white' }}>
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={8}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                                    {/* <Avatar
                                        sx={{
                                            width: 70,
                                            height: 70,
                                            background: TNPSC_COLOR,
                                            fontWeight: 800,
                                            fontSize: '1.5rem',
                                        }}
                                    >
                                        {tnpscData.shortName}
                                    </Avatar> */}
                                    <Box>
                                        <Typography variant="h4" sx={{ fontWeight: 800 }}>
                                            {tnpscData.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                            {tnpscData.type}
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.7 }}>
                                    {tnpscData.about}
                                </Typography>

                                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.7 }}>
                                    {tnpscData.lmsIntro}
                                </Typography>
                            </Grid>

                            {/* Quick Info */}
                            <Grid item xs={12} md={4}>
                                <Stack spacing={2}>
                                    {tnpscData.quickInfo.map((info) => (
                                        <Paper
                                            key={info.label}
                                            sx={{ p: 2, bgcolor: alpha(TNPSC_COLOR, 0.08), borderRadius: 2 }}
                                        >
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Box sx={{ color: TNPSC_COLOR }}>{info.icon}</Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                                        {info.label}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                                                        {info.value}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grow>
            </Container>

            {/* ==================== KEY EXAMINATION AREAS ==================== */}
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <TrophyIcon sx={{ color: TNPSC_COLOR, fontSize: 36 }} />
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        Key Examination Areas
                    </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4, fontSize: '1rem' }}>
                    TNPSC examinations and subjects covered by the platform
                </Typography>

                <Grid container spacing={3}>
                    {tnpscData.examAreas.map((area, i) => (
                        <Grid item xs={12} sm={6} md={6} key={area.exam}>
                            <Grow in timeout={(i + 1) * 120}>
                                <Card
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        height: '100%',
                                        boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
                                        borderTop: `4px solid ${area.color}`,
                                        transition: 'all 0.25s',
                                        '&:hover': {
                                            transform: 'translateY(-6px)',
                                            boxShadow: `0 20px 40px ${alpha(area.color, 0.25)}`,
                                        },
                                    }}
                                >
                                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                                        <Box
                                            sx={{
                                                width: 52,
                                                height: 52,
                                                borderRadius: 2,
                                                bgcolor: alpha(area.color, 0.12),
                                                color: area.color,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 28,
                                            }}
                                        >
                                            {area.icon}
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: area.color, fontSize: '1.2rem' }}>
                                            {area.exam}
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {area.subjects.map((subject) => (
                                            <Chip
                                                key={subject.name}
                                                icon={subject.icon}
                                                label={subject.name}
                                                sx={{
                                                    bgcolor: alpha(area.color, 0.1),
                                                    color: area.color,
                                                    fontWeight: 600,
                                                    fontSize: '0.9rem',
                                                    mb: 1,
                                                    '& .MuiChip-icon': { color: area.color },
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Card>
                            </Grow>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* ==================== LMS SOLUTIONS / FEATURES ==================== */}
            <Box sx={{ bgcolor: '#f8f9fa', py: 6 }}>
                <Container maxWidth="xl">
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                        <LmsIcon sx={{ color: TNPSC_COLOR, fontSize: 36 }} />
                        <Typography variant="h4" sx={{ fontWeight: 800 }}>
                            LMS Solutions
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4, fontSize: '1rem' }}>
                        Key modules we developed for the TNPSC Smart AI platform
                    </Typography>

                    <Grid container spacing={3}>
                        {tnpscData.lmsFeatures.map((feature, i) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={feature.title}>
                                <Grow in timeout={(i + 1) * 60}>
                                    <Card
                                        sx={{
                                            p: 3,
                                            borderRadius: 3,
                                            height: '100%',
                                            boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
                                            transition: 'all 0.25s',
                                            '&:hover': {
                                                transform: 'translateY(-6px)',
                                                boxShadow: `0 20px 40px ${alpha(TNPSC_COLOR, 0.2)}`,
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 52,
                                                height: 52,
                                                borderRadius: 2,
                                                bgcolor: alpha(TNPSC_COLOR, 0.12),
                                                color: TNPSC_COLOR,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 2,
                                            }}
                                        >
                                            {feature.icon}
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: '1.05rem' }}>
                                            {feature.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
                                            {feature.desc}
                                        </Typography>
                                    </Card>
                                </Grow>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ==================== QUESTION BANK MANAGEMENT ==================== */}
            <Container maxWidth="xl" sx={{ py: 6 }}>
                <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, mb: 4 }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                        <QuizIcon sx={{ color: TNPSC_COLOR, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            Question Bank Management
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4, fontSize: '1rem', lineHeight: 1.7 }}>
                        The platform provides a structured question-bank workflow:
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                        {tnpscData.questionBankFlow.map((step, i) => (
                            <React.Fragment key={step}>
                                <Chip
                                    label={step}
                                    sx={{
                                        bgcolor: alpha(TNPSC_COLOR, 0.12),
                                        color: TNPSC_COLOR,
                                        fontWeight: 700,
                                        fontSize: '0.9rem',
                                        mb: 1,
                                        height: 34,
                                        px: 1,
                                    }}
                                />
                                {i < tnpscData.questionBankFlow.length - 1 && (
                                    <Typography sx={{ color: alpha(TNPSC_COLOR, 0.5), fontWeight: 800, mb: 1 }}>
                                        →
                                    </Typography>
                                )}
                            </React.Fragment>
                        ))}
                    </Stack>

                    <Typography variant="body1" sx={{ mt: 4, mb: 2, fontWeight: 600, fontSize: '1rem' }}>
                        Questions can be maintained using different types such as:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {tnpscData.questionTypes.map((type) => (
                            <Chip
                                key={type}
                                label={type}
                                sx={{
                                    bgcolor: 'white',
                                    color: TNPSC_COLOR,
                                    fontWeight: 600,
                                    fontSize: '0.9rem',
                                    mb: 1,
                                    border: `1px solid ${alpha(TNPSC_COLOR, 0.4)}`,
                                }}
                            />
                        ))}
                    </Stack>
                </Paper>

                {/* ==================== AI-POWERED LEARNING ==================== */}
                <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                        <AIIcon sx={{ color: TNPSC_COLOR, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            AI-Powered Learning
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4, fontSize: '1rem', lineHeight: 1.7 }}>
                        The platform can support AI-assisted educational workflows such as:
                    </Typography>

                    <Grid container spacing={2}>
                        {tnpscData.aiFeatures.map((feature, i) => (
                            <Grid item xs={12} sm={6} md={4} key={feature}>
                                <Grow in timeout={(i + 1) * 80}>
                                    <Paper
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: alpha(TNPSC_COLOR, 0.06),
                                            border: `1px solid ${alpha(TNPSC_COLOR, 0.2)}`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            transition: 'all 0.25s',
                                            '&:hover': {
                                                bgcolor: alpha(TNPSC_COLOR, 0.12),
                                                transform: 'translateX(4px)',
                                            },
                                        }}
                                    >
                                        <AIIcon sx={{ color: TNPSC_COLOR, fontSize: 22 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                            {feature}
                                        </Typography>
                                    </Paper>
                                </Grow>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            </Container>

            {/* ==================== ACADEMIC STRUCTURE ==================== */}
            <Container maxWidth="xl" sx={{ pb: 8 }}>
                <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                        <SecurityIcon sx={{ color: TNPSC_COLOR, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            Academic Structure
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4, fontSize: '1rem', lineHeight: 1.7 }}>
                        This structured approach enables TNPSC aspirants to manage their preparation systematically from syllabus learning and topic-based practice through mock examinations, result analysis and continuous performance improvement.
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                        {tnpscData.academicStructure.map((step, i) => (
                            <React.Fragment key={step}>
                                <Chip
                                    label={step}
                                    sx={{
                                        bgcolor: alpha(TNPSC_COLOR, 0.12),
                                        color: TNPSC_COLOR,
                                        fontWeight: 700,
                                        fontSize: '0.9rem',
                                        mb: 1,
                                        height: 34,
                                        px: 1,
                                    }}
                                />
                                {i < tnpscData.academicStructure.length - 1 && (
                                    <Typography sx={{ color: alpha(TNPSC_COLOR, 0.5), fontWeight: 800, mb: 1 }}>
                                        →
                                    </Typography>
                                )}
                            </React.Fragment>
                        ))}
                    </Stack>
                </Paper>
            </Container>

            <HomeFooter />
        </Box>
    );
};

export default NewTNPSCSmartAIPage;